import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { queryKeys } from '@/lib/constants';
import { ROUTES } from '@/lib/constants/routes';
import { getApiErrorMessage } from '@/lib/utils';

import { authApi } from '../api';
import { useAuthStore } from '../stores';
import type { LoginRequest } from '../types';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { user, isAuthenticated, isLoading, setUser, clearUser, setLoading, hasAccess, logout } =
    useAuthStore();

  // Read fresh on every render so logout (which removes the token) immediately
  // flips this to false and disables the profile query — preventing a refetch
  // loop on /users/profile errors.
  const hasToken =
    typeof window !== 'undefined' && !!localStorage.getItem('accessToken');

  // Set loading to false immediately if no token exists
  useEffect(() => {
    if (!hasToken && isLoading) {
      setLoading(false);
    }
  }, [hasToken, isLoading, setLoading]);

  // Fetch user profile - always fetch when we have a token but no user
  const {
    isLoading: isProfileLoading,
    refetch: refetchProfile,
    error: profileError,
  } = useQuery({
    queryKey: queryKeys.auth.user(),
    queryFn: async () => {
      const userData = await authApi.getProfile();
      setUser(userData);
      return userData;
    },
    enabled: hasToken && !user,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  // On profile fetch failure (e.g. invalid/expired token) clear auth state and
  // send the user to login. Handled here as a side effect rather than inside
  // queryFn so we don't re-enter the query and trigger a refetch loop.
  useEffect(() => {
    if (!profileError) return;
    logout();
    queryClient.removeQueries({ queryKey: queryKeys.auth.user() });
    navigate(ROUTES.LOGIN);
  }, [profileError, logout, queryClient, navigate]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: async (response) => {
      const { tokens } = response;

      // Store tokens only - user data comes from profile API
      localStorage.setItem('accessToken', tokens.access);
      localStorage.setItem('refreshToken', tokens.refresh);

      // Set auth cookie for legacy compatibility
      document.cookie = `token=${tokens.access}; path=/`;

      // Fetch user profile from API
      const { data: userData } = await refetchProfile();

      toast.success(`Welcome back, ${userData?.name ?? 'User'}!`);

      const hasDashboardAccess = [
        { resource: 'md-statistic', action: 'get' },
        { resource: 'partner-statistic', action: 'get' },
        { resource: 'accountant-statistic', action: 'get' },
        { resource: 'lawyer-statistic', action: 'get' },
      ].some(({ resource, action }) => hasAccess(resource, action));

      navigate(hasDashboardAccess ? ROUTES.DASHBOARD : ROUTES.CLIENTS);
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error);

      if (message === 'User not found') {
        toast.error('User not found. Please check your credentials.');
      } else {
        toast.error(message || 'Login failed. Please check your credentials.');
      }
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        await authApi.logout();
      } catch {
        // Ignore logout API errors - still clear local state
      }
    },
    onSettled: () => {
      logout();
      queryClient.clear();
      navigate(ROUTES.LOGIN);
      toast.success('You have been logged out.');
    },
  });

  const handleLogin = useCallback(
    (data: LoginRequest) => {
      loginMutation.mutate(data);
    },
    [loginMutation]
  );

  const handleLogout = useCallback(() => {
    logoutMutation.mutate();
  }, [logoutMutation]);

  // Determine actual loading state - only loading if we have a token and are fetching
  const actuallyLoading = hasToken ? isProfileLoading || !user : false;

  return {
    // State
    user,
    isAuthenticated,
    isLoading: actuallyLoading,

    // Actions
    login: handleLogin,
    logout: handleLogout,
    hasAccess,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    loginError: loginMutation.error,
  };
}
